import React, { useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';

import { useAppDispatch, useAppSelector } from '../../app/hook';
import PopoverWithAudio, { IPopoverWithAudioType } from '../../components/common/PopoverWithAudio';
import ProBadge from '../../components/ui/ProBadge';
import { AlertsType } from '../../constants/alerts.constant';
import usePermission from '../../hooks/usePermission';
import AppearanceDisplayName from './AppearanceDisplayName';
import AppearanceEnableSeo from './AppearanceEnableSeo';
import { handleUpdateAppearanceItem, selectAppearanceState } from './appearanceSlice';
import AppearanceVoiceResponseToggle from './AppearanceVoiceResponseToggle';
import AvatarSelector from './AvatarSelector';
import ColorDropDown from './ColorDropDown';
import { IAiAppearance } from './types';

const AppearanceHumanClone = () => {
  const { t, i18n } = useTranslation();

  const dispatch = useAppDispatch();

  const permissions = usePermission();
  const { configs } = useAppSelector(selectAppearanceState);

  const avatarRef = useRef<HTMLInputElement | null>(null);

  const {
    register,
    watch,
    setValue,
    formState: { errors },
    control,
    getValues,
  } = useFormContext<IAiAppearance>();

  const formContext = {
    register,
    setValue,
    watch,
    getValues,
    formState: { errors },
    control,
  };

  // ========================= Avatar ============================ //
  const [avatarsByUser, setAvatarsByUser] = useState<string | ArrayBuffer | null>();

  const handleSelectCustomAvatar = () => {
    if (avatarRef.current) {
      avatarRef.current.value = '';
      avatarRef.current.click();
    }
  };

  const handleVerifyAndSelectCustomAvatar = (e: any) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      if (file.name.length > 150) {
        toast.error(t?.(AlertsType.ISSTKO007, { chars: 150 }));
        return false;
      }

      if (file.size * 0.000001 > 10) {
        toast.error(t(AlertsType.ISSTKO008, { size: 10 }));
        return false;
      }

      if (!['image/jpeg', 'image/jpg', 'image/png', 'image/gif'].includes(file.type)) {
        toast.error(t(AlertsType.ISSTKO009));
        return false;
      }

      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        setAvatarsByUser(reader.result);
        handleUpdateItem('logo', file);
      };
      reader.onerror = error => console.log(error);
    }
  };

  const handleRemoveClientAvatar = () => {
    setAvatarsByUser(null);
    handleUpdateItem('logo', '');
  };

  // ========================= General ============================ //
  const handleUpdateItem = (key: keyof IAiAppearance, value: any) => {
    setValue(key, value);
    dispatch(handleUpdateAppearanceItem({ key, value }));
  };

  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(null);

  useEffect(() => {
    return () => {
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
      }
    };
  }, [currentAudio]);

  const voiceResponsePopData: IPopoverWithAudioType = {
    contentText: t('DashboardSettingCustomizeChat/pop-14'),
  };

  useEffect(() => {
    if (
      configs?.logo &&
      configs.logo !== 'https://cdn.1cdn.app/application/LOUISEBOT/2312171433313_Ellipse_980.png' &&
      configs.logo !== 'https://cdn.1cdn.app/application/LOUISEBOT/24050807084353_Logo_(2).png' &&
      typeof configs.logo !== 'object'
    ) {
      setAvatarsByUser(configs.logo);
      handleUpdateItem('logo', configs?.logo);
    }
  }, [configs?.logo]);

  return (
    <div className="appearance-human-clone-scenario tw-w-full">
      {/* Avatar */}
      <AvatarSelector
        t={t}
        permissions={permissions}
        handleUpdateItem={handleUpdateItem}
        handleRemoveClientAvatar={handleRemoveClientAvatar}
        handleSelectCustomAvatar={handleSelectCustomAvatar}
        handleVerifyAndSelectCustomAvatar={handleVerifyAndSelectCustomAvatar}
        configs={configs}
        avatarsByUser={avatarsByUser}
        avatarRef={avatarRef}
        errors={errors}
      />
      <hr className="tw-border-gray-200 tw-pb-6 ty-m-0" />
      <AppearanceDisplayName errors={errors} register={register} />

      {/* Theme */}
      <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-flex">
        <div className="tw-self-stretch tw-text-gray-700 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">
          {t('DashboardSettingCustomizeChat/l-06')}
        </div>
        <div className="tw-self-stretch tw-justify-start tw-items-center tw-gap-6 tw-flex tw-w-full">
          <div className="tw-grid tw-grid-cols-12 tw-gap-4 tw-w-full">
            <div className="tw-w-full tw-col-span-12">
              <Form.Group>
                <Form.Label className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                  {t('DashboardSettingCustomizeChat/l-12')}
                  {!permissions.settingsPage.widget.customization.theme && <ProBadge className="tw-ms-2" />}
                </Form.Label>
                <Form.Select
                  translate="no"
                  disabled={!permissions.settingsPage.widget.customization.theme}
                  isInvalid={!!errors.theme}
                  {...register('theme', {
                    onChange(event) {
                      dispatch(
                        handleUpdateAppearanceItem({
                          key: 'theme',
                          value: event.target.value,
                        })
                      );
                    },
                  })}
                >
                  <option value={'light'}>{t('DashboardSettingCustomizeChat/l-05')}</option>
                  <option value={'dark'}>{t('DashboardSettingCustomizeChat/l-07')}</option>
                </Form.Select>
                {errors.theme && <span className="tw-text-red-500 tw-text-sm tw-block">{errors.theme?.message}</span>}
              </Form.Group>
            </div>
            <div className="tw-w-full tw-col-span-12">
              <Form.Group>
                <Form.Label className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                  {t('DashboardSettingCustomizeChat/t-01')}
                  {!permissions.settingsPage.widget.customization.colors && <ProBadge className="tw-ms-2" />}
                </Form.Label>
                <Controller
                  control={control}
                  name="color"
                  render={({ field: { onChange, value } }) => (
                    <ColorDropDown
                      acceptableColors={[
                        { label: 'Grey', value: '#6B7280' },
                        { label: 'Red', value: '#EF4444' },
                        { label: 'Orange', value: '#F97316' },
                        { label: 'Yellow', value: '#F59E0B' },
                        { label: 'Green', value: '#10B981' },
                        { label: 'Teal', value: '#14B8A6' },
                        { label: 'Cyan', value: '#06B6D4' },
                        { label: 'Light Blue', value: '#0EA5E9' },
                        { label: 'Blue', value: '#3B82F6' },
                        { label: 'Indigo', value: '#6366F1' },
                        { label: 'Purple (Violet)', value: '#8B5CF6' },
                        { label: 'Pink', value: '#EC4899' },
                      ]}
                      disabled={!permissions.settingsPage.widget.customization.colors}
                      isInvalid={!!errors.color}
                      errorMessage={errors.color?.message}
                      onColorChanged={color => {
                        onChange(color);

                        dispatch(
                          handleUpdateAppearanceItem({
                            key: 'color',
                            value: color,
                          })
                        );
                      }}
                      currentColor={value}
                    />
                  )}
                />
              </Form.Group>
            </div>
          </div>
        </div>
      </div>
      <hr className="tw-border-gray-200 tw-pb-6 ty-m-0" />

      {/* Voice Messages */}
      <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-flex">
        <div className="tw-self-stretch tw-text-gray-700 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">
          {t('DashboardSettingCustomizeChat/l-16')}
        </div>
        <div className="tw-self-stretch tw-justify-start tw-items-start tw-gap-6 tw-flex tw-w-full tw-flex-col">
          <Form.Group className="tw-flex tw-items-center tw-gap-2 tw-mb-4">
            <Form.Check
              disabled={!permissions.settingsPage.widget.customization.voiceCommand}
              type="switch"
              id="isVoiceActive"
              className="tw-flex tw-items-center tw-gap-2 tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
              label={t('DashboardSettingCustomizeChat/allow-send-voice')}
              isInvalid={!!errors.isVoiceActive}
              feedbackType="invalid"
              feedback={errors.isVoiceActive?.message}
              {...register('isVoiceActive', {
                onChange: e => {
                  dispatch(
                    handleUpdateAppearanceItem({
                      key: 'isVoiceActive',
                      value: e.target.checked,
                    })
                  );
                },
              })}
            />
            {!permissions.settingsPage.widget.customization.voiceCommand && <ProBadge className="tw-ms-2" />}
            <PopoverWithAudio
              data={{
                contentText: voiceResponsePopData.contentText,
              }}
            />
          </Form.Group>

          <AppearanceVoiceResponseToggle formContext={formContext} i18n={i18n} permissions={permissions} />

          <AppearanceEnableSeo control={control} errors={errors} register={register} watch={watch} />
        </div>
      </div>

      <hr className="tw-border-gray-200 tw-pb-6 ty-m-0" />
    </div>
  );
};

export default WrappedErrorBoundary(AppearanceHumanClone);
